import React, { useContext, useMemo } from 'react'
import { ConfigurationContext } from '../../../../components/journey-builder/configuration-editor/configuration-context'
import { getTriggerFlags } from '../../../../utils/trigger-flags'
import { extractJourneyTrigger, getJourneyTriggerItemType } from '../../../../_utils/journeys'
import { JourneyType } from '@pushly/models/lib/enums/journeys'
import { Well } from '@pushly/aqe/lib/components'
import { Skeleton } from 'antd'
import { FEAT_ABANDONED_VIEW_CAMPAIGNS, FEAT_SAVED_ITEM_CAMPAIGNS } from '../../../../constants'
import { CatalogItemType } from '@pushly/models/lib/enums'
import AbandonmentSettingsSection from './abandonment-settings-section'
import SubscriberEntryCapSection from './subscriber-entry-cap-section'
import { TriggerSection } from './trigger-section'
import { RecurrenceSection } from './recurrence-section'
import { useTriggerHandlers } from '../../hooks/entry/use-trigger-handlers'
import { JourneyEditableState } from '@pushly/models/lib/enums/journeys'
import { getDefaultCatalogItemType, getDefaultMaxAbandonedItemAgeDays } from '../../utils/defaults'
import { NextOccurrencesFromSchedule } from '../../../../components/scheduling'

// Keep prop type explicit for future extensibility
export type JourneyEntryWellProps = {}

export const JourneyEntryWell: React.FC<JourneyEntryWellProps> = () => {
    const { journey, mode, loading, domain, domainDefaultSegment, editableState, setJourney, setFlags } =
        useContext(ConfigurationContext)

    if (!journey) return null

    // Determine journey and trigger info using existing helpers for parity
    const journeyType = journey.type
    const triggerType = (() => {
        try {
            const trigger = extractJourneyTrigger(journey)
            const trigCfg = trigger?.configuration
            return trigCfg?.type
        } catch {
            return undefined
        }
    })()

    const flags = useMemo(() => getTriggerFlags(triggerType), [triggerType])

    const computeCatalogItemType = useMemo<CatalogItemType>(() => {
        return getJourneyTriggerItemType(journey) ?? getDefaultCatalogItemType(domain)
    }, [journey, domain?.ecommConfig?.itemType])

    const computedDefaultCartItemAgeDaysMax = useMemo<number>(
        () => getDefaultMaxAbandonedItemAgeDays(domain),
        [domain?.ecommConfig?.maxAbandonedItemAgeDays],
    )

    const { handleTriggerTypeChange } = useTriggerHandlers({
        journey: journey,
        catalogItemType: computeCatalogItemType,
        defaultMaxAgeDays: computedDefaultCartItemAgeDaysMax,
        domainDefaultSegment,
        setJourney,
        setFlags,
    })

    // Abandonment-related models
    const currTrigger = extractJourneyTrigger(journey)

    const entryCap = journey.configuration.subscriber_fcap

    return (
        <Well
            className={`journey-entry-well nested mode-${mode} ${journeyType === JourneyType.TRIGGER ? 'triggered' : ''} ${journeyType === JourneyType.RECURRING ? 'recurring' : ''}`}
            title="Entry"
            hideFooter={true}
        >
            <Skeleton loading={loading} active={true} title={false} paragraph={{ rows: 1, width: '100%' }}>
                {journeyType === JourneyType.TRIGGER && (
                    <TriggerSection
                        trigger={currTrigger as any}
                        journeyType={journeyType}
                        hasAbandonedViewAccess={(domain?.flags ?? []).includes(FEAT_ABANDONED_VIEW_CAMPAIGNS)}
                        hasSavedItemsAccess={(domain?.flags ?? []).includes(FEAT_SAVED_ITEM_CAMPAIGNS)}
                        onChange={handleTriggerTypeChange}
                        domainItemType={domain?.ecommConfig?.itemType}
                        disabled={!!journey.id}
                    />
                )}

                {journeyType === JourneyType.RECURRING &&
                    (() => {
                        const schedule = journey.configuration.schedule
                        return (
                            <div className="recurrence-schedule-wrapper">
                                {schedule && (
                                    <RecurrenceSection
                                        schedule={schedule}
                                        editable={editableState !== JourneyEditableState.COMPLETED}
                                        domain={domain}
                                        journeyTimeZone={journey.date_time_zone || 'UTC'}
                                        onUpdateSchedule={(mutate) => {
                                            const cloned = structuredClone(journey)
                                            const sch = cloned.configuration.schedule
                                            if (sch) {
                                                mutate(sch)
                                            }
                                            setJourney(cloned)
                                        }}
                                    />
                                )}
                                <NextOccurrencesFromSchedule
                                    schedule={journey.configuration.schedule}
                                    timeZone={journey.configuration.schedule?.time_zone}
                                    dateStart={journey.date_start}
                                    dateEnd={journey.date_end}
                                />
                            </div>
                        )
                    })()}

                <AbandonmentSettingsSection
                    showAbandonment={flags.isAbandonment}
                    showCooldown={flags.isAbandonedView}
                    showMinimumItemViews={flags.isAbandonedView}
                    showMaxItemAge={flags.isAbandonedCart || flags.isSavedItem}
                    itemType={computeCatalogItemType}
                    triggerType={triggerType}
                    configuration={journey.configuration}
                    triggerConfig={currTrigger}
                    defaultMaxItemAgeDays={computedDefaultCartItemAgeDaysMax}
                    onApply={(apply) => {
                        const cloned = structuredClone(journey)
                        const trig = extractJourneyTrigger(cloned)
                        apply(cloned.configuration, trig)
                        setJourney(cloned)
                    }}
                />

                {!!triggerType && !flags.isSubscribed && (
                    <SubscriberEntryCapSection
                        disabled={editableState === JourneyEditableState.COMPLETED}
                        cap={entryCap}
                        onChange={(next) => {
                            const cloned = structuredClone(journey)
                            cloned.configuration.subscriber_fcap = next
                            setJourney(cloned)
                        }}
                    />
                )}
            </Skeleton>
        </Well>
    )
}

export default JourneyEntryWell
